import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  runStatusOf,
  type ProgramNewsRun,
  type ProgramNewsRunStatus,
} from "@/services/adminProgramNews";
import Term from "./Term";
import { runStatusTerm } from "./glossary";
import { formatDateTime, formatDuration } from "./formatters";
import { headlineCounters } from "./runCounters";
import { describeRunLimit } from "./runLimits";

interface RunRowProps {
  run: ProgramNewsRun;
}

const STATUS_VARIANT: Record<ProgramNewsRunStatus, "outline" | "secondary" | "destructive"> = {
  running: "outline",
  succeeded: "secondary",
  failed: "destructive",
};

/** Milliseconds since `startedAt`, re-read every second while `ticking`. */
function useElapsedMs(startedAt: string | null, ticking: boolean): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [ticking]);

  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  return Number.isNaN(started) ? null : Math.max(0, now - started);
}

const RunRow = ({ run }: RunRowProps) => {
  const status = runStatusOf(run);
  const isRunning = status === "running";
  const counters = headlineCounters(run.type, run.counters);
  const durationMs = run.durationMs ?? run.counters?.durationMs ?? null;
  const elapsedMs = useElapsedMs(run.startedAt, isRunning);
  const limitText = describeRunLimit(run.type, run.limit, run.full);

  return (
    <TableRow>
      <TableCell>
        <Badge variant={run.type === "classify" ? "default" : "secondary"}>
          <Term term={run.type}>{run.type}</Term>
        </Badge>
        {limitText ? (
          <div className="mt-1 text-xs text-muted-foreground whitespace-nowrap">
            {run.full ? <Term term="full_run">{limitText}</Term> : limitText}
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[status]} className="whitespace-nowrap">
          {isRunning ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" /> : null}
          <Term term={runStatusTerm(status)}>{status}</Term>
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{run.trigger}</TableCell>
      <TableCell className="text-sm whitespace-nowrap">{formatDateTime(run.startedAt)}</TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        {isRunning ? (
          <span data-testid="run-elapsed" title="Elapsed so far">
            {formatDuration(elapsedMs)}
            <span className="text-muted-foreground"> so far</span>
          </span>
        ) : (
          formatDuration(durationMs)
        )}
      </TableCell>
      <TableCell className="text-sm">
        {counters.length ? (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {counters.map(({ text, note }) => (
              <span key={text} title={note}>
                {text}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm">
        {run.error ? (
          <span className="text-destructive break-words">{run.error}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
};

export default RunRow;
